import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Button,
  SegmentedControl,
  Text,
  Select,
  Box,
  LoadingOverlay,
  Radio,
  Stack,
  List,
  Table,
  Flex,
  Title,
} from "@mantine/core";
import { IconPlayerPlay, IconCalendarMonth } from "@tabler/icons-react";
import dayjs from "dayjs";

import { DateField, TimeField } from "@components/shared";
import timezones from "@lib/timezones";
import { formatUserCounts, getTimezoneAbbr } from "./helpers";
import { TimeSlots, UserCounts } from "./";

export default function EmailSchedule({
  reqData,
  onSuccess,
  id,
  isStart,
  timezone,
  timezoneOffset,
  entityInfo,
  sendNowAvailable = true,
}) {
  const initialFormValues = {
    start_date: "",
    start_time: "",
    timezone: "",
  };
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [method, setMethod] = useState("later");
  const [userTime, setUserTime] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  const submitDisabled = false;

  useEffect(() => {
    fetchSetupData();

    // Set up polling every 15 seconds
    const interval = setInterval(() => {
      fetchSetupData();
    }, 15000);

    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [formValues.start_date, method, userTime]);

  useEffect(() => {
    if (timezone) {
      setFormValues({
        ...formValues,
        timezone,
      });
    }
  }, [timezone]);

  useEffect(() => {
    if (!sendNowAvailable) {
      setMethod("later");
    }
  }, [sendNowAvailable]);

  function onClose() {
    setFormValues(initialFormValues);
    setLoading(false);
    setMethod("later");
    setSetupLoading(false);
    setSetupData(null);
  }

  function onClick() {
    setLoading(true);
    const req = {
      ...reqData,
      ...formValues,
      start_time: selectedTime,
      user_time: userTime,
    };

    axios
      .post(`/bulk-communicators/${id}/startup/`, req)
      .then(() => {
        setLoading(false);
        toast.success("Scheduled!");
        onClose();
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onStartClick() {
    setLoading(true);
    const req = {
      ...reqData,
      // start_time: dayjs().add(5, "minutes").format("HH:mm"),
      // start_date: dayjs().format("YYYY-MM-DD"),
      // additional_minutes: 5,
      send_now: true,
    };

    axios
      .post(`/bulk-communicators/${id}/startup/`, req)
      .then(() => {
        setLoading(false);
        toast.success("Scheduled!");
        onClose();
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function fetchSetupData() {
    setSetupLoading(true);

    const req = {
      bulk_communicator_id: id,
      timeframe: method,
      user_time: userTime,
      // timezone: formValues.timezone,
      context: "scheduler",
    };

    axios
      .post(`/bulk-communicators/${id}/data-grabber/`, req)
      .then(({ data }) => {
        const res = data.response[0];
        if (!res) {
          return setSetupLoading(false);
        }
        setSetupData(res);
        setFormValues({
          ...formValues,
          start_date:
            method === "later" && formValues.start_date
              ? formValues.start_date
              : res.date,
        });
        setSetupLoading(false);
        if (method === "later" && selectedTime && res.start_hours) {
          const selectedTimeMinutes =
            parseInt(selectedTime.split(":")[0]) * 60 +
            parseInt(selectedTime.split(":")[1]);
          const startHoursMinutes =
            parseInt(res.start_hours.split(":")[0]) * 60 +
            parseInt(res.start_hours.split(":")[1]);

          if (selectedTimeMinutes > startHoursMinutes) {
            setSelectedTime(null);
          }
        }
      })
      .catch((err) => {
        setSetupLoading(false);
        setSetupData(null);
      });
  }

  const entityTimezoneAbbrevation = getTimezoneAbbr(timezoneOffset);

  return (
    <div style={{ position: "relative" }}>
      {/* <LoadingOverlay visible={setupLoading} /> */}
      <Flex justify="center">
        <SegmentedControl
          mb="lg"
          size="sm"
          radius="xl"
          color="dark"
          value={method}
          onChange={(e) => {
            setSelectedTime(null);
            setMethod(e);
          }}
          data={[
            { label: "Send Now", value: "now" },
            // { label: "Send Today", value: "today" },
            { label: "Send Later", value: "later" },
          ]}
        />
      </Flex>
      {method === "now" && (
        <>
          <SendNow
            setupData={setupData}
            loading={loading}
            onClick={onStartClick}
          />
        </>
      )}
      {["later", "today"].includes(method) && (
        <>
          {/* <Box mt="xs">
            <TimeField
              required
              label="Time"
              value={formValues.start_time}
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  start_time: e,
                })
              }
              // min={minTime}
              // max={maxTime}
              error={
                timeAvailableInWindow ? "" : "Selected time is not available."
              }
            />
          </Box> */}
          {selectedTime ? (
            <>
              <Title order={1} align="center">
                {dayjs(formValues.start_date).format("MM/DD/YYYY")}{" "}
                {dayjs()
                  .hour(parseInt(selectedTime.split(":")[0]))
                  .minute(parseInt(selectedTime.split(":")[1]))
                  .format("h:mm A")}
                {userTime ? "" : ` ${entityTimezoneAbbrevation}`}
              </Title>
              <Flex justify="center">
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => setSelectedTime(null)}
                >
                  Select a different time
                </Button>
              </Flex>
              {setupData && (
                <Box mt="xl">
                  <SelectedTimePreview
                    time={selectedTime}
                    date={formValues.start_date}
                    userCounts={setupData?.user_counts}
                    // timezone={formValues.timezone}
                    timezone={entityTimezoneAbbrevation}
                    timezoneOffset={timezoneOffset}
                    userTime={userTime}
                  />
                </Box>
              )}
              <Button
                mt="md"
                size="md"
                onClick={onClick}
                loading={loading}
                color="green"
                fullWidth
                disabled={submitDisabled}
              >
                Schedule
              </Button>
            </>
          ) : (
            <>
              <Box
                style={{
                  border: "1px solid var(--mantine-color-gray-3)",
                  padding: "10px 10px 10px 10px",
                  borderRadius: "var(--mantine-radius-md)",
                  backgroundColor: "var(--mantine-color-gray-0)",
                }}
              >
                <DateField
                  label="Date"
                  size="md"
                  required
                  min={setupData?.date}
                  disabled={method === "today"}
                  value={formValues.start_date}
                  onChange={(e) => {
                    // Validate that the year is not before setupData.date's year
                    if (setupData?.date && e) {
                      const selectedYear = dayjs(e).year();
                      const setupDataYear = dayjs(setupData.date).year();

                      if (selectedYear < setupDataYear) {
                        // Don't update the form value if year is too early
                        return;
                      }
                    }

                    setFormValues({
                      ...formValues,
                      start_date: e,
                    });
                  }}
                />
                <Stack mb="md" mt="md">
                  {[
                    {
                      label: `Send emails in ${
                        entityInfo.organization_name || entityInfo.location_name
                      } timezone (${entityTimezoneAbbrevation})`,
                      value: false,
                    },
                    { label: "Send emails in user timezone", value: true },
                  ].map((option, index) => (
                    <Radio
                      size="sm"
                      key={index}
                      label={option.label}
                      onChange={(e) => setUserTime(option.value)}
                      checked={userTime === option.value}
                    />
                  ))}
                </Stack>
                {/* {!userTime && (
                  <>
                    <Select
                      label="Timezone"
                      data={timezones}
                      value={formValues.timezone}
                      placeholder="Select one..."
                      mt="xs"
                      size="md"
                      required
                      onChange={(e) =>
                        setFormValues({
                          ...formValues,
                          timezone: e,
                        })
                      }
                    />
                  </>
                )} */}
              </Box>
              <Box mt="md" mb="xl">
                {/* <Text mb="xs" align="center" size="lg" fw={600}>
                  Select a time to send
                  {userTime ? "" : ` (in ${entityTimezoneAbbrevation})`}
                </Text> */}
                <TimeSlots
                  startTime={setupData?.start_hours}
                  endTime={setupData?.end_hours}
                  onSelect={(time) => setSelectedTime(time)}
                  isEmail
                />
              </Box>
            </>
          )}
        </>
      )}
    </div>
  );
}

const SelectedTimePreview = ({
  time,
  date,
  userCounts = [],
  timezone,
  timezoneOffset,
  userTime,
}) => {
  const formattedUserCounts = formatUserCounts({
    userCounts,
    date,
    time,
    type: "email",
    timezoneOffset,
    sendAtUserTime: userTime,
  });

  return (
    <div style={{ minHeight: "100px" }}>
      <Box>
        <UserCounts timezones={formattedUserCounts} />
      </Box>
    </div>
  );
};

const SendNow = ({ setupData, loading, onClick }) => {
  const userCounts = formatUserCounts({
    userCounts: setupData.user_counts,
    date: dayjs(new Date()).format("YYYY-MM-DD"),
    time: dayjs(setupData.send_time_utc).format("HH:mm"),
    type: "email",
    timezoneOffset: new Date().getTimezoneOffset() / -60,
    sendAtUserTime: false,
  });

  const unavailableUserCounts = userCounts.filter(
    (userCount) => !userCount.available
  );
  const totalUnavailable = unavailableUserCounts.reduce(
    (acc, userCount) => acc + userCount.count,
    0
  );

  const canSend = true;

  return (
    <>
      {canSend && (
        <Box mb="xl">
          <Text align="center" size="xl">
            The next available send time is:
          </Text>
          {setupData.send_time_utc && (
            <Title order={1} align="center">
              {dayjs(setupData.send_time_utc).format("MM/DD/YYYY hh:mm A")}
            </Title>
          )}
        </Box>
      )}
      {totalUnavailable > 0 && (
        <Text align="center" mt="md" size="lg" c="red" fw={600}>
          ({totalUnavailable}) users spanning ({unavailableUserCounts.length})
          timezones will not receive the message.
        </Text>
      )}
      {setupData && (
        <>
          {setupData.user_counts.length > 0 && (
            <Box mt="md" mb="md">
              <UserCounts timezones={userCounts} />
            </Box>
          )}
          {canSend && (
            <Button
              mt="lg"
              onClick={onClick}
              loading={loading}
              color="green"
              size="md"
              fullWidth
            >
              Confirm
            </Button>
          )}
        </>
      )}
    </>
  );
};
